import { Typography } from '@mui/material';
import successCheckMark from '@root/assets/img/auth/mail-sent-checkmark.svg';
import { Button } from '@root/components/v3/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@root/components/v3/dialog';
import { Input } from '@root/components/v3/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@root/components/v3/select';
import { Textarea } from '@root/components/v3/textarea';
// import CircularCloseButton from '@root/components/v2/ui-elements/CircularCloseButton';
// import Select from '@root/components/v2/ui-elements/Select';
import { axiosPost } from '@root/services/axios/axiosPost';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
// import { Button } from 'reactstrap';
import JIRAIcon from '@root/assets/svg/CICD/JIRAIcon';
import Loader from '@root/components/common/Loader';
import { useQueryClient } from '@tanstack/react-query';
import { Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import './config.scss';
import { CommonDatePicker } from '@root/components/common/CommonDatePicker';
import { isEmpty } from 'lodash';
import { identityHumanTabPath } from '@root/constants/moduleTabs';

function JiraConfiguration({
  setOpen,
  open,
  rowData,
  mode = 'Workload',
  assetId,
  setShowDetailsPanel,
  isParentWorkload = false,
  workloadAllResourceIds = [],
  isTicketIdPresent = false,
  isFetchingTicketDetails = false,
  orgId,
  isFetchingOrgId,
  isFetchingProjectDetails,
  projects,
}) {
  const [error, setError] = useState('');

  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [issuesOptions, setIssuesOptions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selectedIssue, setSelectedIssue] = useState('');
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const formatToLocalDate = dateValue => {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const title =
    mode === 'Compliance'
      ? `Compliance Detected For ${
          rowData.resource_type.includes('role') ? 'role' : rowData.resource_type.includes('policy') ? 'policy' : 'user'
        } ${rowData.resource_name}`
      : mode === 'ComplianceMain'
        ? `Compliance Detected For ${rowData.master_rule_id}`
        : mode === 'RiskMain'
          ? `Risk Detected For ${rowData.master_rule_id}`
          : mode === 'Risk'
            ? `Risk Detected For ${
                rowData.resource_type.includes('role')
                  ? 'role'
                  : rowData.resource_type.includes('policy')
                    ? 'policy'
                    : 'user'
              } ${rowData.resource_name}`
            : mode === 'Kubernetes'
              ? `Optimize over privileged ${rowData.identity_details.identity_type} ${rowData.identity_details.identity_name}`
              : mode === 'Identity'
                ? `Optimize over privileged ${rowData.type === 'role' ? 'role' : 'user'} ${rowData.name}`
                : mode === 'Events'
                  ? // ? 'Optimize over privileged'
                    'Threat Detected on CyberQshield'
                  : mode === 'Attack Path'
                    ? 'Potential Attack Path Detected on CyberQshield'
                    : mode === 'WorkloadMain'
                      ? `Vulnerability detected for ${rowData.cve_id}`
                      : mode === 'IACAlerts'
                        ? `IaC Misconfiguration Detected For ${rowData.repository}`
                        : mode === 'DAST'
                          ? `DAST Vulnerability detected For ${rowData.name} in ${rowData.checkmarx_environment_id}`
                          : mode === 'Code_Scanning'
                            ? `Code Scanning Vulnerability detected For ${rowData.name} in ${rowData.checkmarx_environment_id}`
                            : mode === 'DatabaseSecurity'
                              ? `Database Security Finding ${rowData?.check_id} on ${rowData?.resource?.type ?? '-'} ${
                                  rowData?.resource?.id ?? ''
                                }`
                              : `Vulnerability detected for ${rowData.cve_id}`;
  const description =
    mode === 'Compliance'
      ? `CyberQ Shield has detected Compliance For ${rowData.resource_id} for ${rowData.resource_name}`
      : mode === 'ComplianceMain'
        ? `CyberQ Shield has detected Compliance For ${rowData.master_rule_id}`
        : mode === 'RiskMain'
          ? `CyberQ Shield has detected Risk for ${rowData.master_rule_id}`
          : mode === 'Risk'
            ? `CyberQ Shield has detected Risk for ${rowData.resource_id} for ${rowData.rule_description}`
            : mode === 'Kubernetes'
              ? `CyberQ Shield has identified unused & Over-privileged for ${rowData.identity_details.identity_type} ${rowData.identity_details.identity_name}  ${rowData.user.resource.id} . Refer link <https://cyberqshield.seclogic.io/kubernetes-live/identity> for details on how to resolve the issue.`
              : mode === 'Identity'
                ? `CyberQ Shield has identified unused & Over-privileged for ${rowData.type === 'role' ? 'role' : 'user'} ${
                    rowData.name
                  } ${rowData.resource_id} . Refer link <https://cyberqshield.seclogic.io${identityHumanTabPath(
                    rowData.type === 'role' ? 'roles' : 'users',
                  )}> for details on how to resolve the issue.`
                : mode === 'Events'
                  ? `CyberQ Shield has identified Threat for rule ${rowData.event_name}.  Refer Link https://cyberqshield.seclogic.io/events/all?tab=Threat and go to Threats tab for details on how to resolve the issue.`
                  : mode === 'Attack Path'
                    ? `CyberQ Shield has detected attack path for rule ${rowData.rule_name}. Refer Link https://cyberqshield.seclogic.io/attack-path-analysis for details on how to resolve the issue.`
                    : mode === 'IACAlerts'
                      ? `CyberQ Shield has detected an Infrastructure as Code misconfiguration for ${rowData.repository_id}. This configuration violates security best practices and could lead to potential security risks. Refer to link <https://cyberqshield.seclogic.io/code/repository-scanning> for details on how to resolve the issue.`
                      : mode === 'DAST'
                        ? `CyberQ Shield has detected a DAST Vulnerability for ${rowData.name} in ${rowData.checkmarx_environment_id}. This configuration violates security best practices and could lead to potential security risks. Refer to link <https://cyberqshield.seclogic.io/code/dast-scanning> for details on how to resolve the issue.`
                        : mode === 'Code_Scanning'
                          ? `CyberQ Shield has detected a Code Scanning Vulnerability for ${rowData.name} in ${rowData.checkmarx_environment_id}. This configuration violates security best practices and could lead to potential security risks. Refer to link <https://cyberqshield.seclogic.io/code/code-scanning> for details on how to resolve the issue.`
                          : mode === 'DatabaseSecurity'
                            ? `CyberQ Shield has detected a Database Security finding for check ${
                                rowData?.check_id
                              } on ${rowData?.resource?.type ?? '-'} ${
                                rowData?.resource?.id ?? ''
                              }. Refer to link <https://cyberqshield.seclogic.io/database-security/overview> for details on how to resolve the issue.`
                            : `CyberQ Shield has detected Vulnerability ${rowData.cve_id} for ${
                              isParentWorkload
                                ? `\n ${workloadAllResourceIds?.map(id => `- ${id}`).join('\n')}`
                                : (rowData.resource_id ?? rowData?.cluster ?? '-')
                            }`;

  const user = useSelector(state => state.user);
  const orgID = useMemo(() => {
    if (isFetchingOrgId) return '';
    return orgId?.data?.orgId;
  }, [isFetchingOrgId, orgId?.data?.orgId]);
  const clearAll = () => {
    setShowSuccess(false);
    setError(false);
    setSelectedIssue('');
    setSelectedAssignee('');
    setSelectedPriority('');
    setStartDate('');
    setEndDate('');
    setDueDate('');
  };

  const handleCreate = async () => {
    let payload =
      mode === 'Compliance'
        ? {
            orgId: orgID,
            currentUserEmail: user.info.user.email,
            asset_id: assetId,
            issuePayload: {
              projectKey: selectedProject,
              title: title,
              description: description,
              issueType: selectedIssue,
              priority: selectedPriority,
              assigneeEmail: selectedAssignee,
              startDate: startDate,
              endDate: endDate,
              dueDate: dueDate,
            },
            accountId: rowData.environment_id,
            scanId: rowData.scan_id ?? '',
            customInfo: {
              resourceId: rowData.resource_id,
              resourceName: rowData.resource_name,
              resourceType: rowData.resource_type,
              region: 'global',
            },
          }
        : mode === 'ComplianceMain'
          ? {
              orgId: orgID,
              currentUserEmail: user.info.user.email,
              asset_id: assetId,
              issuePayload: {
                projectKey: selectedProject,
                title: title,
                description: description,
                issueType: selectedIssue,
                priority: selectedPriority,
                assigneeEmail: selectedAssignee,
                startDate: startDate,
                endDate: endDate,
                dueDate: dueDate,
              },
              accountId: rowData?.selectedExpansionData?.environment_id ?? '',
              scanId: rowData?.selectedExpansionData?.scan_id ?? '',
              customInfo: {
                master_rule_id: rowData?.master_rule_id,
                region: 'global',
              },
            }
          : mode === 'Risk'
            ? {
                orgId: orgID,
                currentUserEmail: user.info.user.email,
                asset_id: assetId,
                issuePayload: {
                  projectKey: selectedProject,
                  title: title,
                  description: description,
                  issueType: selectedIssue,
                  priority: selectedPriority,
                  assigneeEmail: selectedAssignee,
                  startDate: startDate,
                  endDate: endDate,
                  dueDate: dueDate,
                },
                accountId: rowData.environment_id,
                scanId: rowData.scan_id ?? '',
                customInfo: {
                  resourceId: rowData.resource_id,
                  resourceName: rowData.resource_name,
                  resourceType: rowData.resource_type,
                  region: 'global',
                },
              }
            : mode === 'RiskMain'
              ? {
                  orgId: orgID,
                  currentUserEmail: user.info.user.email,
                  asset_id: assetId,
                  issuePayload: {
                    projectKey: selectedProject,
                    title: title,
                    description: description,
                    issueType: selectedIssue,
                    priority: selectedPriority,
                    assigneeEmail: selectedAssignee,
                    startDate: startDate,
                    endDate: endDate,
                    dueDate: dueDate,
                  },
                  accountId: '',
                  scanId: '',
                  customInfo: {
                    master_rule_id: rowData?.master_rule_id,
                    region: 'global',
                  },
                }
              : mode === 'Kubernetes'
                ? {
                    orgId: orgID,
                    currentUserEmail: user.info.user.email,
                    asset_id: assetId,
                    issuePayload: {
                      projectKey: selectedProject,
                      title: title,
                      description: description,
                      issueType: selectedIssue,
                      priority: selectedPriority,
                      assigneeEmail: selectedAssignee,
                      startDate: startDate,
                      endDate: endDate,
                      dueDate: dueDate,
                    },
                    accountId: rowData.user.account_id,
                    scanId: rowData.scanId ?? '',
                    customInfo: {
                      resourceId: rowData.user.resource.id,
                      resourceName: rowData.user.resource.name,
                      resourceType: rowData.user.resource.type,
                      region: 'global',
                    },
                  }
                : mode === 'WorkloadMain'
                  ? {
                      orgId: orgID,
                      currentUserEmail: user.info.user.email,
                      asset_id: assetId,
                      issuePayload: {
                        projectKey: selectedProject,
                        title: title,
                        description: description,
                        issueType: selectedIssue,
                        priority: selectedPriority,
                        assigneeEmail: selectedAssignee,
                        startDate: startDate,
                        endDate: endDate,
                        dueDate: dueDate,
                      },
                      accountId: rowData.account_id,
                      scanId: rowData.scanId,
                      customInfo: {
                        resourceId: rowData.resource_id,
                        resourceName: rowData.name,
                        resourceType: rowData.type,
                        region: 'global',
                      },
                    }
                  : mode === 'Identity'
                    ? {
                        orgId: orgID,
                        asset_id: assetId,
                        currentUserEmail: user.info.user.email,
                        issuePayload: {
                          projectKey: selectedProject,
                          title: title,
                          description: description,
                          issueType: selectedIssue,
                          priority: selectedPriority,
                          assigneeEmail: selectedAssignee,
                          startDate: startDate,
                          endDate: endDate,
                          dueDate: dueDate,
                        },
                        accountId: rowData.accountId,
                        scanId: rowData.scanId,
                        customInfo: {
                          resourceId: rowData.resource_id,
                          resourceName: rowData.name,
                          resourceType: rowData.type,
                          region: 'global',
                        },
                      }
                    : mode === 'Identity'
                      ? {
                          orgId: orgID,
                          asset_id: assetId,
                          currentUserEmail: user.info.user.email,
                          issuePayload: {
                            projectKey: selectedProject,
                            title: title,
                            description: description,
                            issueType: selectedIssue,
                            priority: selectedPriority,
                            assigneeEmail: selectedAssignee,
                            startDate: startDate,
                            endDate: endDate,
                            dueDate: dueDate,
                          },
                          accountId: rowData.accountId,
                          scanId: rowData.scanId,
                          customInfo: {
                            resourceId: rowData.resource_id,
                            resourceName: rowData.name,
                            resourceType: rowData.type,
                            region: 'global',
                          },
                        }
                      : mode === 'Events'
                        ? {
                            orgId: orgID,
                            currentUserEmail: user.info.user.email,
                            asset_id: assetId,
                            issuePayload: {
                              projectKey: selectedProject,
                              title: title,
                              description: description,
                              issueType: selectedIssue,
                              priority: selectedPriority,
                              assigneeEmail: selectedAssignee,
                              startDate: startDate,
                              endDate: endDate,
                              dueDate: dueDate,
                            },
                            accountId: rowData.environment_id,
                            scanId: rowData.scanId,
                            customInfo: {
                              resourceId: rowData.resource_id,
                              resourceName: rowData.name,
                              resourceType: rowData.type,
                              region: 'global',
                            },
                          }
                        : mode === 'Attack Path'
                          ? {
                              orgId: orgID,
                              currentUserEmail: user.info.user.email,
                              asset_id: assetId,
                              issuePayload: {
                                projectKey: selectedProject,
                                title: title,
                                description: description,
                                issueType: selectedIssue,
                                priority: selectedPriority,
                                assigneeEmail: selectedAssignee,
                                startDate: startDate,
                                endDate: endDate,
                                dueDate: dueDate,
                              },
                              accountId: rowData.account_id,
                              scanId: rowData.scanId,
                              customInfo: {
                                resourceId: rowData.resource_id,
                                resourceName: rowData.name,
                                resourceType: rowData.type,
                                region: rowData.region[0],
                              },
                            }
                          : mode === 'DAST'
                            ? {
                                orgId: orgID,
                                currentUserEmail: user.info.user.email,
                                asset_id: assetId,
                                issuePayload: {
                                  projectKey: selectedProject,
                                  title: title,
                                  description: description,
                                  issueType: selectedIssue,
                                  priority: selectedPriority,
                                  assigneeEmail: selectedAssignee,
                                  startDate: startDate,
                                  endDate: endDate,
                                  dueDate: dueDate,
                                },
                                accountId: rowData.checkmarx_environment_id,
                                scanId: rowData.scanId,
                                source: 'dast',
                                customInfo: {
                                  resourceId: rowData.resource_id,
                                  resourceName: rowData.name,
                                  resourceType: rowData.type,
                                  region: rowData.region,
                                },
                              }
                            : mode === 'Code_Scanning'
                              ? {
                                  orgId: orgID,
                                  currentUserEmail: user.info.user.email,
                                  asset_id: assetId,
                                  issuePayload: {
                                    projectKey: selectedProject,
                                    title: title,
                                    description: description,
                                    issueType: selectedIssue,
                                    priority: selectedPriority,
                                    assigneeEmail: selectedAssignee,
                                    startDate: startDate,
                                    endDate: endDate,
                                    dueDate: dueDate,
                                  },
                                  accountId: rowData.checkmarx_environment_id,
                                  scanId: rowData.scanId,
                                  customInfo: {
                                    resourceId: rowData.resource_id,
                                    resourceName: rowData.name,
                                    resourceType: rowData.type,
                                    region: rowData.region,
                                  },
                                }
                              : mode === 'IACAlerts'
                                ? {
                                    orgId: orgID,
                                    currentUserEmail: user.info.user.email,
                                    asset_id: assetId,
                                    issuePayload: {
                                      projectKey: selectedProject,
                                      title: title,
                                      description: description,
                                      issueType: selectedIssue,
                                      priority: selectedPriority,
                                      assigneeEmail: selectedAssignee,
                                      startDate: startDate,
                                      endDate: endDate,
                                      dueDate: dueDate,
                                    },
                                    // accountId: rowData.repository_id,
                                    scanId: rowData.scan_id,
                                    customInfo: {
                                      repository: rowData.repository,
                                      branch_name: rowData.branch_name,
                                    },
                                  }
                                : mode === 'DatabaseSecurity'
                                  ? {
                                      orgId: orgID,
                                      currentUserEmail: user.info.user.email,
                                      asset_id: assetId,
                                      issuePayload: {
                                        projectKey: selectedProject,
                                        title: title,
                                        description: description,
                                        issueType: selectedIssue,
                                        priority: selectedPriority,
                                        assigneeEmail: selectedAssignee,
                                        startDate: startDate,
                                        endDate: endDate,
                                        dueDate: dueDate,
                                      },
                                      accountId: rowData?.project_id ?? '',
                                      scanId: rowData?.scan_id ?? '',
                                      source: 'database_security',
                                      customInfo: {
                                        checkId: rowData?.check_id,
                                        resourceId: rowData?.resource?.id,
                                        resourceType: rowData?.resource?.type,
                                        category: rowData?.category,
                                        severity: rowData?.severity,
                                        region: 'global',
                                      },
                                    }
                                  : {
                                    orgId: orgID,
                                    asset_id: assetId,
                                    currentUserEmail: user.info.user.email,
                                    issuePayload: {
                                      projectKey: selectedProject,
                                      title: title,
                                      description: description,
                                      issueType: selectedIssue,
                                      priority: selectedPriority,
                                      assigneeEmail: selectedAssignee,
                                      startDate: startDate,
                                      endDate: endDate,
                                      dueDate: dueDate,
                                    },
                                    accountId: rowData.environment_id,
                                    scanId: rowData.scan_id,
                                    customInfo: {
                                      resourceId: rowData.resource_id,
                                      resourceName: rowData.resource_name,
                                      resourceType: rowData.resource_type,
                                      region: rowData.region,
                                    },
                                  };

    // console.log('Payload:', payload);
    try {
      setIsCreatingTicket(true);
      const res = await axiosPost('jira/create_issue', payload, true);
      queryClient.invalidateQueries(['get_ticket_details']);

      if (!res?.data?.error) {
        setShowSuccess(true);
        toast.success('Ticket created successfully');
        setError(false);
      }
    } catch (err) {
      setError(true);
    } finally {
      setIsCreatingTicket(false);
      setOpen(false);
      setShowDetailsPanel(false);
    }
  };

  useEffect(() => {
    if (isFetchingOrgId || isFetchingProjectDetails) {
      return; // Wait until the orgId and projects data are ready
    }

    setLoading(true);

    (async () => {
      const res2 = await axiosPost('jira/issue_meta', { orgId: orgId?.data?.orgId }, true);

      // Find the selected project from the provided projects prop
      const selectedProjectData = projects?.data?.find(project => project.projectKey === res2?.data?.data?.projectKey);

      setSelectedProject(res2?.data?.data?.projectKey || '');
      setProjectOptions([selectedProjectData]);
      setSelectedIssue(res2?.data?.data?.defaultSelected?.issueTypes || '');
      setIssuesOptions(res2?.data?.data?.payload?.issueTypes?.map(issue => Object.values(issue)[0])?.filter(Boolean));
      setPriorityOptions(res2?.data?.data?.payload?.issuePriority?.map(prio => Object.keys(prio)[0])?.filter(Boolean));
      setAssigneeOptions(res2?.data?.data?.payload?.userInfo);

      setLoading(false);
    })();
  }, [user, isFetchingOrgId, isFetchingProjectDetails, projects, orgId]);

  const handleSelectProjectChange = useCallback(
    async project => {
      setSelectedProject(project);

      const res = await axiosPost(
        'jira/project_info',
        {
          orgId: orgId?.data?.orgId,
          projectKey: project[0],
        },
        true,
      );

      setIssuesOptions(res?.data?.data?.issueTypes?.map(issue => Object.values(issue)[0]));
      setAssigneeOptions(res?.data?.data?.userInfo);
      setPriorityOptions(res?.data?.data?.issuePriority?.map(prio => Object.keys(prio)[0]));
    },
    [setSelectedProject, setIssuesOptions, orgId],
  );

  // console.log('selectedIssue', selectedIssue, selectedPriority, selectedAssignee);

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          setOpen(false);
          setShowDetailsPanel(false);
        }
      }}>
      <DialogContent className="jira_workload max-w-[800px] min-w-[630px] p-0 gap-0">
        {isTicketIdPresent ? (
          isFetchingTicketDetails ? (
            <div style={{ width: '630px', height: '400px' }}>
              <Loader />
            </div>
          ) : (
            <>
              <DialogHeader className="p-[15px] px-[30px] flex w-full pb-0">
                <div style={{ height: '100%', display: 'flex', gap: ' 4px', alignItems: 'center' }}>
                  <JIRAIcon height="12px" width="12px" />
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: '15px',
                      fontFamily: 'inherit',
                      fontWeight: '500',
                    }}>
                    JIRA
                  </Typography>
                </div>
                <div style={{ flexGrow: '1' }} />
              </DialogHeader>

              <div className="Jira__Created__Ticket " style={{ margin: '30px', width: '550px', padding: '15px 30px' }}>
                <Typography
                  variation="body2"
                  sx={{
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    fontWeight: '500',
                  }}>
                  Ticket Details : {isTicketIdPresent}
                </Typography>
              </div>
              <DialogFooter className="mr-[30px]">
                <DialogClose>
                  <Button
                    onClick={e => {
                      setOpen(false);
                      setShowSuccess(false);
                      e.stopPropagation();
                    }}>
                    Done
                  </Button>
                </DialogClose>
              </DialogFooter>
            </>
          )
        ) : !showSuccess ? (
          <>
            <DialogHeader className="p-[0px]">
              <DialogTitle asChild>
                <div
                  style={{
                    padding: '15px 30px',
                    display: 'flex',
                    flexDirection: 'row',
                  }}>
                  <div style={{ flexGrow: '1', display: 'flex', alignItems: 'center' }}>
                    <p className="modal_heading m-0 text-[16px] font-bold"> Create JIRA Ticket</p>
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="CustomScrollbar overflow-y-auto overflow-x-hidden w-full max-h-[70vh]">
              {loading || isFetchingTicketDetails ? (
                <div style={{ height: '70vh' }}>
                  <Loader />
                </div>
              ) : (
                <div className="">
                  <div className="card_container">
                    <div>
                      <p className="input_heading m-0"> Project*</p>
                      <div>
                        <Select value={selectedProject} onValueChange={handleSelectProjectChange}>
                          <SelectTrigger className="w-[562px]">
                            <SelectValue placeholder="Select a Project" />
                          </SelectTrigger>
                          <SelectContent>
                            {projectOptions?.filter(Boolean)?.map(option => (
                              <SelectItem key={option?.projectKey} value={option?.projectKey}>
                                {option?.projectName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <p className="input_heading m-0"> Issue Type*</p>
                      <Select value={selectedIssue} onValueChange={setSelectedIssue}>
                        <SelectTrigger className="w-[562px]">
                          <SelectValue placeholder="Select Issue Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {issuesOptions?.map(
                            issue =>
                              issue && (
                                <SelectItem key={issue} value={issue}>
                                  {issue}
                                </SelectItem>
                              ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="input_heading m-0"> Summary</p>
                      <Input className="w-[562px]" disabled placeholder="Issue Summary" value={title} />
                    </div>
                    <div>
                      <p className="input_heading m-0"> Description*</p>
                      <Textarea
                        className="w-[562px]"
                        style={{
                          height: isParentWorkload ? '150px' : '127px',
                          overflowY: 'auto',
                        }}
                        value={description}
                        readOnly
                      />
                    </div>
                    <div>
                      <p className="input_heading m-0"> Assignee*</p>
                      <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                        <SelectTrigger className="w-[562px]">
                          <SelectValue placeholder="Select Assignee" />
                        </SelectTrigger>
                        <SelectContent>
                          {assigneeOptions?.map(
                            assignee =>
                              assignee?.email && (
                                <SelectItem key={assignee.email} value={assignee.email}>
                                  {assignee.name}
                                </SelectItem>
                              ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="input_heading m-0"> Priority*</p>
                      <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                        <SelectTrigger className="w-[562px]">
                          <SelectValue placeholder="Select Priority" />
                        </SelectTrigger>
                        <SelectContent>
                          {priorityOptions?.map(
                            prio =>
                              prio && (
                                <SelectItem key={prio} value={prio}>
                                  {prio}
                                </SelectItem>
                              ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="input_heading m-0">Start Date*</p>
                      <CommonDatePicker
                        value={!isEmpty(startDate) ? startDate : null}
                        onChange={date => {
                          const formatted = formatToLocalDate(date);
                          setStartDate(formatted === null ? '' : formatted);
                        }}
                        minDate={new Date()}
                      />
                    </div>

                    <div>
                      <p className="input_heading m-0">End Date*</p>
                      <CommonDatePicker
                        value={!isEmpty(endDate) ? endDate : null}
                        onChange={date => {
                          const formatted = formatToLocalDate(date);
                          setEndDate(formatted === null ? '' : formatted);
                        }}
                        minDate={startDate}
                        maxDate={dueDate}
                      />
                    </div>
                    <div>
                      <p className="input_heading m-0">Due Date*</p>
                      <CommonDatePicker
                        value={!isEmpty(dueDate) ? dueDate : null}
                        onChange={date => {
                          const formatted = formatToLocalDate(date);
                          setDueDate(formatted === null ? '' : formatted);
                        }}
                        minDate={startDate}
                        maxDate={endDate}
                      />
                    </div>
                    {error && (
                      <div
                        style={{
                          color: 'red',
                          fontSize: '14px',
                          width: '100%',
                          textAlign: 'right',
                          fontFamily: 'Manrope',
                          fontWeight: 500,
                        }}>
                        Something went wrong, please try again!
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="mr-[30px]">
              <DialogClose>
                <Button
                  variant="outline"
                  onClick={() => {
                    clearAll();
                    setOpen(false);
                    setShowDetailsPanel(false);
                  }}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                // color="primary"
                onClick={handleCreate}
                disabled={
                  !selectedIssue || !selectedPriority || !selectedAssignee || !startDate || !endDate || !dueDate
                  // selectedIssue.some(
                  //   item =>
                  //     item === null ||
                  //     item === undefined ||
                  //     (typeof item === 'object' && Object.keys(item).length === 0),
                  // ) ||
                  // selectedPriority.some(
                  //   item =>
                  //     item === null ||
                  //     item === undefined ||
                  //     (typeof item === 'object' && Object.keys(item).length === 0),
                  // ) ||
                  // selectedAssignee.some(
                  //   item =>
                  //     item === null ||
                  //     item === undefined ||
                  //     (typeof item === 'object' && Object.keys(item).length === 0),
                  // ) ||
                  // selectedIssue.length === 0 ||
                  // selectedPriority.length === 0 ||
                  // selectedAssignee.length === 0
                }>
                {isCreatingTicket ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Spinner animation="border" size="sm" className="p-2" color="white" style={{ color: 'white' }} />
                    <span>Creating Ticket...</span>
                  </div>
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div style={{ padding: '15px', display: 'flex', width: '100%', paddingBottom: '0' }}>
              <div style={{ flexGrow: '1' }} />
            </div>
            <div className="success-box" style={{ margin: '1rem', width: '95%' }}>
              <img src={successCheckMark} className="success-checkmark" />
              <Typography
                variation="body2"
                sx={{
                  fontSize: '15px',
                  fontFamily: 'inherit',
                  color: '#1D8661',
                  fontWeight: '500',
                }}>
                Ticket created successfully!
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'end' }}>
              <Button
                variant={'default'}
                onClick={() => {
                  setOpen(false);
                  setShowSuccess(false);
                }}>
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default JiraConfiguration;
